<template>
  <div v-if="open" class="guide-overlay">
    <!-- úvod alebo medzikrok s avatarom -->
    <BetweenOverlay
      v-if="showIntro || isBetween"
      :variant="showIntro ? 'intro' : 'branch'"
      :avatar-url="avatarUrl"
      :visible-total="visibleTotal"
      :plan-items="planItems"
      :plan-summary="planSummary"
      :plan-hint="planHint"
      :title="between?.title"
      :text="between?.text"
      :content-blocks="between?.contentBlocks || []"
      :options="between?.options || []"
      @start="startFromFirst"
      @choose="onChoose"
      @close="close"
    />

    <!-- klasické kroky (ring + tooltip) -->
    <StepOverlay v-else @close="close" />
  </div>
</template>

<script>
import { computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import {
  tour,
  enableGuestTour,
  disableGuestTour,
} from "@/components/features/Navigacia/tour/tour.nologged.js";
import BetweenOverlay from "@/components/features/Navigacia/tour/ui/BetweenOverlay.vue";
import StepOverlay from "@/components/features/Navigacia/tour/ui/StepOverlay.vue";
import avatarGuideDefault from "@/assets/images/gallery/avatar-guide-tight.png";

export default {
  name: "GuideOverlay",
  components: { BetweenOverlay, StepOverlay },
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const open = computed(() => tour.state.open);
    const steps = computed(() => tour.state.steps || []);
    const isLoggedIn = computed(() => !!store.state.user);

    const hasRealSteps = computed(
      () =>
        isLoggedIn.value && steps.value.some((step) => step?.type !== "intro")
    );

    const syncTourMode = (loggedIn) => {
      if (loggedIn) {
        disableGuestTour();
        return;
      }
      enableGuestTour();
      if (tour.state.open && tour.state.mode !== "between") tour.close();
    };

    watch(
      isLoggedIn,
      (loggedIn) => {
        syncTourMode(loggedIn);
      },
      { immediate: true }
    );

    // intro = prvý step je typu "intro" a sme na indexe 0
    const showIntro = computed(
      () =>
        open.value && steps.value[0]?.type === "intro" && tour.state.index === 0
    );

    // medzikrok po dokončení etapy
    const isBetween = computed(() => tour.state.mode === "between");
    const between = computed(() => tour.state.between || null);

    // v plániku nezapočítavame úvod
    const visibleTotal = computed(() => {
      if (!hasRealSteps.value) return 0;
      return steps.value[0]?.type === "intro"
        ? Math.max(0, steps.value.length - 1)
        : steps.value.length;
    });

    const planBlueprint = computed(() => [
      {
        label: t("tour.plan.blueprint.home.label"),
        bridgeLabel: t("tour.plan.blueprint.home.bridge"),
        points: 50,
      },
      {
        label: t("tour.plan.blueprint.scores.label"),
        bridgeLabel: t("tour.plan.blueprint.scores.bridge"),
        points: 150,
      },
      {
        label: t("tour.plan.blueprint.videos.label"),
        bridgeLabel: t("tour.plan.blueprint.videos.bridge"),
        points: 100,
      },
      {
        label: t("tour.plan.blueprint.classroom.label"),
        bridgeLabel: t("tour.plan.blueprint.classroom.bridge"),
        points: 100,
      },
      {
        label: t("tour.plan.blueprint.done.label"),
        bridgeLabel: t("tour.plan.blueprint.done.bridge"),
        points: null,
      },
    ]);

    const tryParsePoints = (value) => {
      const num = Number(value);
      if (Number.isFinite(num) && num > 0) return Math.round(num);
      return null;
    };

    const resolvePoints = (stage, idx) => {
      if (tour.state.program?.hidePoints) return null;
      if (stage && typeof stage === "object") {
        const direct = tryParsePoints(stage.points);
        if (direct !== null) return direct;
        const reward = tryParsePoints(stage.rewardPoints);
        if (reward !== null) return reward;
        const alt = tryParsePoints(stage.pointsReward);
        if (alt !== null) return alt;
      }

      const blueprint = planBlueprint.value;
      const fallback = tryParsePoints(blueprint[idx]?.points);
      return fallback;
    };

    const DIACRITIC_REGEX = new RegExp(
      "[ÁÄĂÀÂÃÅĄÆČĆĎÉĚËÈÊĘÍÌÎÏĹĽŁŇŃÓÒÔÖÕŐŘŔŚŠŤÚÙÛÜŮÝŸŹŻŽáäăàâãåąæčćďéěëèêęíìîïĺľłňńóòôöõőřŕśšťťúùûüůýÿźżžßẞ]",
      "g"
    );
    const DIACRITIC_MAP = {
      Á: "A",
      Ä: "A",
      Ă: "A",
      À: "A",
      Â: "A",
      Ã: "A",
      Å: "A",
      Ą: "A",
      Æ: "AE",
      Č: "C",
      Ć: "C",
      Ď: "D",
      É: "E",
      Ě: "E",
      Ë: "E",
      È: "E",
      Ê: "E",
      Ę: "E",
      Í: "I",
      Ì: "I",
      Î: "I",
      Ï: "I",
      Ĺ: "L",
      Ľ: "L",
      Ł: "L",
      Ň: "N",
      Ń: "N",
      Ó: "O",
      Ò: "O",
      Ô: "O",
      Ö: "O",
      Õ: "O",
      Ő: "O",
      Ř: "R",
      Ŕ: "R",
      Ś: "S",
      Š: "S",
      Ť: "T",
      Ú: "U",
      Ù: "U",
      Û: "U",
      Ü: "U",
      Ů: "U",
      Ý: "Y",
      Ÿ: "Y",
      Ź: "Z",
      Ż: "Z",
      Ž: "Z",
      á: "a",
      ä: "a",
      ă: "a",
      à: "a",
      â: "a",
      ã: "a",
      å: "a",
      ą: "a",
      æ: "ae",
      č: "c",
      ć: "c",
      ď: "d",
      é: "e",
      ě: "e",
      ë: "e",
      è: "e",
      ê: "e",
      ę: "e",
      í: "i",
      ì: "i",
      î: "i",
      ï: "i",
      ĺ: "l",
      ľ: "l",
      ł: "l",
      ň: "n",
      ń: "n",
      ó: "o",
      ò: "o",
      ô: "o",
      ö: "o",
      õ: "o",
      ő: "o",
      ř: "r",
      ŕ: "r",
      ś: "s",
      š: "s",
      ť: "t",
      ú: "u",
      ù: "u",
      û: "u",
      ü: "u",
      ů: "u",
      ý: "y",
      ÿ: "y",
      ź: "z",
      ż: "z",
      ž: "z",
      ß: "ss",
      ẞ: "ss",
    };

    const stripDiacritics = (value) => {
      const str = String(value || "");
      let ascii = str;

      if (typeof ascii.normalize === "function") {
        try {
          ascii = ascii.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        } catch (error) {
          ascii = str;
        }
      }

      return ascii.replace(
        DIACRITIC_REGEX,
        (char) => DIACRITIC_MAP[char] || char
      );
    };

    const normalizeKey = (raw) =>
      stripDiacritics(raw)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

    const stageKeyFrom = (stage, idx) => {
      if (!stage) return `stage-${idx + 1}`;
      if (stage.key) return stage.key;
      if (stage.name) return stage.name;
      if (stage.label) return stage.label;
      return `stage-${idx + 1}`;
    };

    const baseLabelFor = (stage, idx) => {
      if (stage?.label && String(stage.label).trim()) return stage.label.trim();
      if (stage?.name && String(stage.name).trim()) return stage.name.trim();
      const blueprint = planBlueprint.value;
      const fallback = blueprint[idx]?.label;
      if (fallback && String(fallback).trim()) return fallback.trim();
      return t("tour.plan.fallbackStage", { number: idx + 1 });
    };

    const planItems = computed(() => {
      if (!hasRealSteps.value) return [];

      const rawStageIndex = Number(tour.state.stageIndex);
      const stageIndex = Math.max(
        0,
        Number.isFinite(rawStageIndex) ? rawStageIndex : 0
      );
      const isBetween = tour.state.mode === "between";
      const programStages = Array.isArray(tour.state.program?.stages)
        ? tour.state.program.stages
        : [];
      const blueprint = planBlueprint.value;

      const completedKeys = new Set(
        (Array.isArray(tour.state.completedStageKeys)
          ? tour.state.completedStageKeys
          : []
        )
          .map((key) => normalizeKey(key))
          .filter(Boolean)
      );

      const items = programStages.map((stage, idx) => {
        const key = normalizeKey(stageKeyFrom(stage, idx));
        const label = baseLabelFor(stage, idx);
        const bridgeLabel =
          (typeof stage?.bridgeLabel === "string" &&
            stage.bridgeLabel.trim()) ||
          (typeof stage?.branch?.planBridgeLabel === "string" &&
            stage.branch.planBridgeLabel.trim()) ||
          (typeof blueprint[idx]?.bridgeLabel === "string" &&
            blueprint[idx].bridgeLabel.trim()) ||
          "";

        const item = {
          key,
          index: idx,
          label,
          bridgeLabel,
          status: "upcoming",
          points: resolvePoints(stage, idx),
        };

        if (completedKeys.has(key)) {
          item.status = "done";
        } else if (!isBetween && idx === stageIndex) {
          item.status = "current";
        }

        return item;
      });

      if (!items.length) {
        const fallbackLabel = baseLabelFor(null, 0);
        return [
          {
            key: normalizeKey(stageKeyFrom(null, 0)),
            index: 0,
            label: fallbackLabel,
            bridgeLabel:
              (typeof planBlueprint.value[0]?.bridgeLabel === "string" &&
                planBlueprint.value[0].bridgeLabel.trim()) ||
              "",
            status: "current",
            points: resolvePoints(null, 0),
          },
        ];
      }

      // During between screens add a placeholder tile for the upcoming area.
      if (isBetween && !tour.state.program?.hidePlanPlaceholder) {
        const currentStage =
          programStages[Math.min(stageIndex, items.length - 1)] || null;
        const placeholderLabel =
          (typeof currentStage?.bridgeLabel === "string" &&
            currentStage.bridgeLabel.trim()) ||
          (typeof blueprint[items.length]?.label === "string" &&
            blueprint[items.length].label.trim()) ||
          "";

        if (placeholderLabel) {
          items.push({
            key: `placeholder-${items.length}`,
            index: items.length,
            label: placeholderLabel,
            bridgeLabel: "",
            status: "upcoming",
            placeholder: true,
            points: resolvePoints(null, items.length),
          });
        }
      }

      return items;
    });

    const planSummary = computed(() => {
      if (!planItems.value.length) return "";
      const labels = planItems.value
        .map((item) => item.label)
        .filter((label) => !!label);
      if (!labels.length) return "";
      return labels.join(" · ");
    });

    const planHint = computed(() => {
      const active = planItems.value.find(
        (item) => item.status === "current" && !item.placeholder
      );
      if (!active) return "";
      return `${active.index + 1}. ${active.label}`;
    });

    const avatarUrl = computed(() => {
      if (isBetween.value && between.value?.avatar) return between.value.avatar;
      if (
        typeof window !== "undefined" &&
        window.__haTourAvatar &&
        window.__haTourAvatar !== ""
      )
        return window.__haTourAvatar;
      return avatarGuideDefault;
    });

    const startFromFirst = async () => {
      const firstReal = steps.value[0]?.type === "intro" ? 1 : 0;
      await tour.goto(firstReal, { force: true });
    };

    const onChoose = async (opt) => {
      await tour.betweenChoose(opt || null);
    };

    const close = () => tour.close();

    onMounted(() => {
      window.haTour = {
        start: (i = 0, opts = {}) => tour.start({ ...opts, startIndex: i }),
        stop: () => tour.close(),
        next: () => tour.next(),
        prev: () => tour.prev(),
        goto: (i = 0) => tour.goto(i),
        isOpen: () => tour.state.open,
      };
      window.__haTourReady = true;
      window.dispatchEvent(new CustomEvent("ha.tour.ready"));
    });
    onBeforeUnmount(() => {
      window.__haTourReady = false;
      window.haTour = undefined;
    });

    return {
      open,
      showIntro,
      isBetween,
      between,
      visibleTotal,
      planItems,
      planSummary,
      planHint,
      avatarUrl,
      startFromFirst,
      onChoose,
      close,
    };
  },
};
</script>

<style>
/* len root kontajner (zdieľajú ho obe podkomponenty) */
.guide-overlay {
  position: fixed;
  inset: 0;
  z-index: 3990;
  background: transparent;
}
</style>
